/**
 * Field canonicalizers — single owner for the "what value does this field
 * actually store" contract.
 *
 * Programmatic writes (place autofill, lane prefill, edit-load) bypass the
 * masked-input keystroke path and would otherwise inject raw API values the
 * validator rejects or that disagree with what a user can type. Route every
 * such write through the matching `toCanonical*` so the stored value always
 * satisfies the validator.
 *
 * All functions are idempotent — a no-op on already-canonical input.
 */

/**
 * Bare 10-digit phone (validator contract `/^\d{10}$/`). Also trims a leading
 * US country code: an 11-digit `1XXXXXXXXXX` is unambiguously `+1`-prefixed
 * (US area codes never start with 1), and the 10-slot input mask would
 * otherwise render its first 10 digits as a plausible-looking wrong number
 * while validation rejects the hidden 11th digit.
 */
export const toCanonicalPhone = (value) => {
  if (value == null) return ''
  const digits = String(value).replace(/\D/g, '')
  return digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits
}

/**
 * Trimmed email. Trim only — the local part is technically case-sensitive, so
 * we deliberately do NOT lowercase. A stray leading/trailing space is the one
 * thing `z.string().email()` would otherwise reject on an autofilled value.
 */
export const toCanonicalEmail = (value) =>
  value == null ? '' : String(value).trim()

/**
 * Trimmed zip/postal code. Trim only — `ZIP_REGEX` (see zipValidation.js) is
 * case-insensitive and accepts US-5 / ZIP+4 / Canadian, so we deliberately do
 * NOT uppercase or reformat.
 */
export const toCanonicalZip = (value) =>
  value == null ? '' : String(value).trim()

const CANONICALIZERS = {
  phone: toCanonicalPhone,
  email: toCanonicalEmail,
  zip: toCanonicalZip,
}

/**
 * `setValue` wrapper that canonicalizes by the field's last path segment.
 * Non-canonical fields pass through unchanged, so it is safe to route any
 * write through it.
 *
 * @param {Function} setValue - react-hook-form setValue
 * @param {string} path - RHF field path, e.g. `legs.0.contact.phone`
 * @param {*} value - raw value to write
 * @param {Object} [options] - forwarded to setValue (e.g. { shouldDirty: true })
 */
export const setCanonical = (setValue, path, value, options) => {
  const fn = CANONICALIZERS[path.split('.').pop()]
  setValue(path, fn ? fn(value) : value, options)
}
